import type { TrainingCourse, TrainingQuestion } from "./trainings";

/*
 * Die festen Inhalte der Schulungen. Sie sind für alle Firmen gleich und lassen sich nicht ändern; Firmen ergänzen
 * Hinweise, eigene Fragen und eigene Abschnitte (siehe trainings.ts). Gesetze, Vorschriften und Normen sind nur benannt, nie zitiert.
 */

const single = (id: string, text: string, options: string[], correct: number, picto?: string): TrainingQuestion => ({
  id,
  text,
  options,
  correct: [correct],
  ...(picto ? { picto } : {}),
});
const multi = (id: string, text: string, options: string[], correct: number[]): TrainingQuestion => ({ id, text, options, correct });

export const TRAINING_COURSES: TrainingCourse[] = [
  {
    id: "arbeitsschutz-allgemein",
    version: 3,
    title: "Allgemeine Arbeitsschutz-Unterweisung",
    kicker: "Jährliche Unterweisung",
    description:
      "Die Grundregeln für alle im Betrieb: Rechte und Pflichten, Rettungswege, Notfall und Erste Hilfe, Brandschutz, Ordnung. Dazu Module, die Ihr Betrieb je nach Arbeitsplatz zuschaltet: Schutzausrüstung, Lärm, Gefahrstoffe, Elektrik.",
    minutes: 30,
    basis: ["Arbeitsschutzgesetz (§ 12)", "DGUV Vorschrift 1 (§ 4)", "DGUV Information 211-005", "ASR A2.3 und ASR A1.3 (Fluchtwege, Sicherheitskennzeichnung)"],
    confirm:
      "Ich habe die Inhalte dieser Unterweisung verstanden. Ich weiß, an wen ich mich mit Fragen wenden kann, und beachte die Regeln bei meiner Arbeit.",
    sections: [
      {
        id: "pflichten",
        sources: ["BGHM-Fachseminar „Wirkungsvolle Unterweisung“ (sifa.dguv.de); DGUV Information 211-005"],
        kind: "basis",
        topics: ["ziel", "maengel", "ansprechpersonen"],
        title: "Ihre Rechte und Pflichten",
        summary: "Warum unterwiesen wird, was Beschäftigte tun müssen, Mängel melden, wer bei Fragen hilft.",
        noteHint: "Zum Beispiel: Welche Gefahren gibt es an Ihren Arbeitsplätzen? Wer ist Ersthelfer, Sicherheitsbeauftragter, Fachkraft für Arbeitssicherheit, Betriebsarzt?",
        slides: [
          {
            title: "Warum diese Unterweisung?",
            text: "Ihr Betrieb muss Sie über die Gefahren an Ihrem Arbeitsplatz informieren: zu Beginn, bei Änderungen und mindestens einmal im Jahr. Diese Schulung ist ein Teil davon.",
            scene: "hello",
          },
          {
            title: "Ihre Pflichten",
            text: "Befolgen Sie die Anweisungen zum Arbeitsschutz, benutzen Sie die bereitgestellte Schutzausrüstung und verwenden Sie Maschinen und Werkzeuge nur so, wie sie gedacht sind.",
            scene: "point",
          },
          {
            title: "Gefahren sofort melden",
            text: "Schäden an Geräten, fehlende Absperrungen oder gefährliche Situationen melden Sie sofort Ihrem Vorgesetzten. Bei unmittelbarer Gefahr dürfen und sollen Sie die Arbeit unterbrechen und sich in Sicherheit bringen.",
            pictos: ["warn-allgemein"],
            scene: "alarm",
          },
          {
            title: "Alkohol, Drogen, Medikamente",
            text: "Niemand darf sich in einen Zustand bringen, in dem er sich oder andere gefährdet. Manche Medikamente machen müde oder langsam: Fragen Sie im Zweifel Ihren Arzt und sagen Sie Ihrem Vorgesetzten Bescheid.",
            scene: "think",
          },
          {
            title: "Wer hilft Ihnen weiter?",
            text: "Im Betrieb gibt es Ansprechpersonen für Arbeitsschutz: Ersthelfer, Sicherheitsbeauftragte, die Fachkraft für Arbeitssicherheit und den Betriebsarzt. Nicht jeder Betrieb hat alle. Ihr Betrieb sagt Ihnen, wer es bei Ihnen ist. Fragen zu stellen gehört zur Unterweisung dazu.",
            scene: "hello",
          },
        ],
        questions: [
          single(
            "kabel",
            "Sie bemerken, dass das Kabel einer Bohrmaschine beschädigt ist. Was tun Sie?",
            ["Weiterarbeiten, solange sie noch läuft", "Nicht mehr benutzen, kennzeichnen und dem Vorgesetzten melden", "Das Kabel selbst mit Isolierband flicken"],
            1,
          ),
          single("wie-oft", "Wie oft muss die Unterweisung mindestens stattfinden?", ["Nur einmal bei der Einstellung", "Mindestens einmal im Jahr und bei Änderungen", "Alle fünf Jahre"], 1),
          multi(
            "aussagen",
            "Welche Aussagen stimmen?",
            ["Ich muss bereitgestellte Schutzausrüstung benutzen.", "Bei unmittelbarer Gefahr darf ich mich in Sicherheit bringen.", "Für Arbeitsschutz ist allein der Chef zuständig."],
            [0, 1],
          ),
        ],
      },
      {
        id: "rettungswege",
        sources: ["ASR A2.3 „Fluchtwege und Notausgänge, Flucht- und Rettungsplan“ (BAuA), Abschnitte 4, 6 und 9"],
        kind: "basis",
        topics: ["rettungswege"],
        title: "Rettungswege und Notausgänge",
        summary: "Dem grünen Zeichen folgen, Wege und Ausgänge frei halten, zur Sammelstelle gehen.",
        noteHint: "Zum Beispiel: Wo ist der Sammelplatz? Welche Ausgänge gibt es in Werkstatt, Büro und Lager? Wo hängt der Rettungsplan?",
        slides: [
          {
            title: "Der Weg nach draußen",
            text: "Rettungswege führen Sie im Notfall ins Freie. Das grüne Zeichen mit dem laufenden Männchen zeigt den Weg zum Notausgang. Folgen Sie ihm, auch wenn es ein Umweg scheint: Es ist der sichere Weg.",
            pictos: ["fluchtweg"],
            scene: "walk",
          },
          {
            title: "Immer frei, nie verstellt",
            text: "Türen im Rettungsweg müssen sich von innen ohne Schlüssel öffnen lassen. Stellen Sie nichts davor ab: keine Paletten, Kartons oder Fahrzeuge, auch nicht „nur kurz“.",
            pictos: ["fluchtweg", "warn-stolpern"],
            scene: "point",
          },
          {
            title: "Die Sammelstelle",
            text: "Auf der Sammelstelle wird gezählt, ob alle da sind. Gehen Sie nie zurück ins Gebäude, auch nicht, um etwas zu holen. Sagen Sie sofort Bescheid, wenn jemand fehlt.",
            pictos: ["sammelstelle"],
            scene: "alarm",
          },
          {
            title: "Der Rettungsplan",
            text: "Im Betrieb hängt ein Flucht- und Rettungsplan. Der Betrieb informiert regelmäßig, möglichst einmal im Jahr, und übt die Räumung. Prüfen Sie am ersten Tag: Wo bin ich gerade, wo ist der nächste Ausgang, und wo ist ein zweiter, falls der erste blockiert ist?",
            scene: "think",
          },
        ],
        questions: [
          single("zeichen", "Was bedeutet dieses Zeichen?", ["Hier ist ein Restaurant", "Rettungsweg und Notausgang: hier geht es nach draußen", "Hier ist das Lager"], 1, "fluchtweg"),
          single(
            "verstellt",
            "Eine Palette steht kurz vor einer Notausgangstür. Was tun Sie?",
            ["Stehen lassen, jemand holt sie bestimmt gleich", "Sofort wegräumen oder melden: Der Weg muss frei sein", "Die Tür abschließen"],
            1,
          ),
          single(
            "alarm",
            "Der Alarm geht los. Was ist richtig?",
            ["Zum Ausgang und zur Sammelstelle gehen und nicht zurückkehren", "Erst meine Sachen holen", "Am Arbeitsplatz warten"],
            0,
            "sammelstelle",
          ),
        ],
      },
      {
        id: "notfall",
        sources: ["BGW-Verbandbuch (DGUV Vorschrift 1, § 24 Abs. 6)"],
        kind: "basis",
        topics: ["unfaelle", "standorte", "doku-eh"],
        title: "Notfall und Erste Hilfe",
        summary: "Notruf absetzen, Ersthelfer und Verbandkasten finden, Verletzungen und Unfälle melden.",
        noteHint: "Zum Beispiel: Namen der Ersthelfer, Ort des Verbandkastens und des Verbandbuchs, nächster Durchgangsarzt.",
        slides: [
          {
            title: "Notruf 112",
            text: "Rettungsdienst und Feuerwehr erreichen Sie unter 112. Sagen Sie, wo es passiert ist, was passiert ist und wie viele Verletzte es gibt. Legen Sie erst auf, wenn die Leitstelle keine Fragen mehr hat.",
            pictos: ["notruftelefon"],
            scene: "alarm",
          },
          {
            title: "Ersthelfer und Verbandkasten",
            text: "Merken Sie sich, wer im Betrieb Ersthelfer ist und wo der Verbandkasten hängt, auch im Fahrzeug. Helfen Sie, so gut Sie können: Nichts tun ist der einzige Fehler.",
            pictos: ["erste-hilfe"],
            scene: "point",
          },
          {
            title: "Jede Verletzung eintragen",
            text: "Auch kleine Verletzungen wie ein Schnitt gehören ins Verbandbuch. So lässt sich später belegen, dass es ein Arbeitsunfall war, falls sich die Wunde entzündet. Die Einträge werden fünf Jahre aufbewahrt.",
            scene: "think",
          },
          {
            title: "Unfälle melden",
            text: "Melden Sie jeden Arbeitsunfall sofort Ihrem Vorgesetzten. Wer nach einem Arbeitsunfall zum Arzt muss, geht zu einem Durchgangsarzt.",
            scene: "check",
          },
        ],
        questions: [
          single("nummer", "Welche Nummer wählen Sie für Rettungsdienst und Feuerwehr?", ["112", "115", "116 117"], 0, "notruftelefon"),
          single(
            "verbandbuch",
            "Warum tragen Sie auch kleine Verletzungen ins Verbandbuch ein?",
            ["Damit später belegt ist, dass es ein Arbeitsunfall war", "Das ist nicht nötig", "Damit der Verbandkasten nachgefüllt wird"],
            0,
          ),
          multi(
            "notruf",
            "Was gehört zu einem guten Notruf?",
            ["Wo ist es passiert?", "Was ist passiert und wie viele Verletzte gibt es?", "Sofort auflegen, sobald die Adresse genannt ist"],
            [0, 1],
          ),
          single("kreuz", "Was zeigt dieses Zeichen an?", ["Eine Apotheke", "Erste Hilfe: Hier gibt es Verbandmaterial oder Hilfe", "Einen Notausgang"], 1, "erste-hilfe"),
        ],
      },
      {
        id: "brandschutz",
        sources: ["BGN-Unterweisungshilfe „Brandschutz“ (bgn.de)"],
        kind: "basis",
        topics: ["unfaelle", "standorte"],
        title: "Brandschutz",
        summary: "Brände verhindern, im Brandfall richtig handeln, Feuerlöscher und Arbeiten mit Funkenflug.",
        noteHint: "Zum Beispiel: Wo hängen die Feuerlöscher und Brandmelder? Wer ist Brandschutzhelfer? Wo wird geraucht?",
        slides: [
          {
            title: "Brände verhindern",
            text: "Rauchen und offenes Feuer sind nur dort erlaubt, wo es ausdrücklich erlaubt ist. Halten Sie Brennbares von Wärmequellen fern, schalten Sie Geräte nach der Arbeit aus und lassen Sie Müll nicht liegen.",
            pictos: ["rauchen-verboten", "feuer-verboten"],
            scene: "point",
          },
          {
            title: "Wenn es brennt",
            text: "Ruhe bewahren, andere warnen, Notruf 112. Menschenrettung geht vor Brandbekämpfung. Schließen Sie Türen und Fenster, benutzen Sie keinen Aufzug und gehen Sie über die gekennzeichneten Fluchtwege zum Sammelplatz. Dort wird geprüft, ob alle da sind.",
            pictos: ["brandmelder", "aufzug-brand"],
            scene: "alarm",
          },
          {
            title: "Feuerlöscher",
            text: "Wissen Sie, wo der nächste Feuerlöscher hängt? Bekämpfen Sie nur Entstehungsbrände. Beachten Sie die Windrichtung, halten Sie Abstand und löschen Sie das Brandgut, nicht die Flammen. Fettbrände nie mit Wasser löschen. Ein benutzter Löscher wird sofort gemeldet und wieder aufgefüllt.",
            pictos: ["feuerloescher"],
            scene: "point",
          },
          {
            title: "Funkenflug",
            text: "Schweißen, Trennschleifen oder Löten außerhalb der Werkstatt nur mit Erlaubnis des Vorgesetzten. Brennbares vorher entfernen, Löscher bereithalten und danach die Stelle kontrollieren.",
            pictos: ["feuer-verboten"],
            scene: "think",
          },
        ],
        questions: [
          multi("brand", "Es brennt. Was ist richtig?", ["Andere warnen und den Notruf absetzen", "Den Aufzug nehmen, das geht schneller", "Zum Sammelplatz gehen"], [0, 2]),
          single(
            "fett",
            "In der Küche brennt heißes Fett in einer Pfanne. Was tun Sie nicht?",
            ["Mit Wasser löschen", "Energiezufuhr abschalten und Notruf absetzen", "Sich selbst nicht in Gefahr bringen"],
            0,
          ),
          single(
            "loeschen",
            "Wann löschen Sie selbst?",
            ["Immer, egal wie groß das Feuer ist", "Nur bei einem kleinen Brand und ohne mich zu gefährden", "Nie, auch nicht einen brennenden Papierkorb"],
            1,
          ),
          single("aufzug", "Was bedeutet dieses Zeichen?", ["Aufzug nur für Lasten", "Im Brandfall den Aufzug nicht benutzen", "Aufzug ist defekt"], 1, "aufzug-brand"),
        ],
      },
      {
        id: "psa",
        kind: "optional",
        topics: ["psa"],
        title: "Persönliche Schutzausrüstung",
        summary: "Welche Schutzausrüstung es gibt, wann sie Pflicht ist und wie man sie pflegt.",
        noteHint: "Zum Beispiel: Welche Schutzausrüstung ist bei Ihnen Pflicht? Wo bekommt man Ersatz?",
        slides: [
          {
            title: "Was zählt dazu?",
            text: "Sicherheitsschuhe, Helm, Schutzbrille, Gehörschutz, Handschuhe, Atemschutz und Absturzsicherung. Ihr Betrieb stellt sie kostenlos bereit.",
            pictos: ["kopfschutz", "augenschutz", "fussschutz", "handschutz"],
            scene: "hello",
          },
          {
            title: "Wann ist sie Pflicht?",
            text: "Überall dort, wo die Betriebsanweisung oder ein blaues, rundes Gebotsschild es verlangt. Die Pflicht gilt auch für kurze Arbeiten: Gerade dabei passieren viele Unfälle.",
            pictos: ["gehoerschutz", "atemschutz", "schutzkleidung"],
            scene: "point",
          },
          {
            title: "Vor jeder Benutzung prüfen",
            text: "Sehen Sie Ihre Schutzausrüstung vor der Arbeit kurz an. Beschädigte Teile wie eine gesprungene Brille tauschen Sie aus, bevor Sie weiterarbeiten.",
            pictos: ["augenschutz"],
            scene: "think",
          },
          {
            title: "Keine Handschuhe an drehenden Teilen",
            text: "An Bohrmaschinen, Drehbänken und anderen drehenden Werkzeugen können Handschuhe erfasst werden und die Hand hineinziehen. Dort arbeiten Sie ohne Handschuhe.",
            pictos: ["handschutz"],
            scene: "alarm",
          },
        ],
        questions: [
          single("schild", "Was bedeutet dieses Zeichen?", ["Helm tragen ist Pflicht", "Hier kann man Helme ausleihen", "Helme sind hier verboten"], 0, "kopfschutz"),
          single("brille", "Ihre Schutzbrille hat einen Sprung. Was tun Sie?", ["Weiter tragen", "Ohne Brille weiterarbeiten", "Ersatz holen oder melden und erst dann weiterarbeiten"], 2, "augenschutz"),
          single(
            "bohren",
            "Sie arbeiten an einer Ständerbohrmaschine. Was gilt für Handschuhe?",
            ["Keine Handschuhe, weil sie erfasst werden können", "Immer dicke Handschuhe tragen", "Handschuhe nur bei Kälte"],
            0,
          ),
        ],
      },
      {
        id: "laerm",
        sources: ["DGUV Fachbereich PSA, Fragen und Antworten zum Gehörschutz (dguv.de); LärmVibrationsArbSchV"],
        kind: "optional",
        topics: ["psa"],
        title: "Lärm und Gehörschutz",
        summary: "Lärm erkennen, Gehörschutz richtig tragen, Vorsorge nutzen.",
        noteHint: "Zum Beispiel: Welche Maschinen oder Bereiche sind Lärmbereiche? Wo gibt es Gehörschutz?",
        slides: [
          {
            title: "Lärm schädigt leise",
            text: "Lärm zerstört Hörzellen oft schleichend und unumkehrbar: Man merkt es erst, wenn es zu spät ist. Ab einem Dauerlärm von 80 dB(A) muss Ihr Betrieb Gehörschutz bereitstellen, ab 85 dB(A) müssen Sie ihn tragen, schon ab 80 dB(A) sollen Sie ihn tragen.",
            pictos: ["gehoerschutz"],
            scene: "alarm",
          },
          {
            title: "Lärmbereiche",
            text: "Bereiche mit Tragepflicht sind mit dem blauen Gebotszeichen gekennzeichnet. Betreten Sie sie nie ohne Gehörschutz, auch nicht kurz. Schon wenige Minuten oder ein lauter Knall können Ihr Gehör schädigen.",
            pictos: ["gehoerschutz"],
            scene: "point",
          },
          {
            title: "Richtig getragen",
            text: "Gehörschutz schützt nur, wenn er richtig sitzt. Stöpsel gehören fest in den Gehörgang, Kapseln müssen rundum abdichten: Haare, Brillenbügel oder ein Helm dürfen keine Lücke lassen. Verschlissene Teile tauschen Sie aus.",
            pictos: ["gehoerschutz"],
            scene: "check",
          },
          {
            title: "Vorsorge nutzen",
            text: "Wer im Lärm arbeitet, bekommt arbeitsmedizinische Vorsorge angeboten, ab 85 dB(A) ist sie Pflicht. Sie ist vertraulich. Melden Sie sich auch, wenn Sie Ohrgeräusche oder schlechteres Hören bemerken.",
            scene: "think",
          },
        ],
        questions: [
          single("zeichen", "Was bedeutet dieses Zeichen?", ["Hier ist es besonders leise", "Gehörschutz benutzen", "Musik hören ist erlaubt"], 1, "gehoerschutz"),
          single("wert", "Ab welchem Dauerlärm müssen Sie Gehörschutz tragen?", ["Ab 85 dB(A)", "Ab 40 dB(A)", "Erst ab 120 dB(A)"], 0),
          single(
            "kurz",
            "Sie müssen nur kurz in einen Lärmbereich. Was tun Sie?",
            ["Auch kurz: Gehörschutz aufsetzen", "Ohne Gehörschutz, es dauert ja nicht lange", "Nur ein Ohr schützen"],
            0,
          ),
        ],
      },
      {
        id: "gefahrstoffe",
        sources: ["BG ETEM Gefahrstoff-Wissenstest (medien.bgetem.de); BG BAU Unterweisungsnachweis Gefahrstoffe"],
        kind: "optional",
        topics: ["arbeitsstoffe"],
        title: "Gefahrstoffe sicher handhaben",
        summary: "Gefahrstoff-Piktogramme lesen, Betriebsanweisungen beachten, sicher umgehen, im Notfall richtig handeln.",
        noteHint: "Zum Beispiel: Welche Gefahrstoffe setzen Sie ein? Wo liegen Betriebsanweisungen und Sicherheitsdatenblätter? Wo ist die Augenspülung?",
        slides: [
          {
            title: "Was sind Gefahrstoffe?",
            text: "Reiniger, Lösemittel, Kleber, Schaum, Farben, Säuren: Viele Stoffe im Betrieb können giftig, ätzend, entzündlich oder gesundheitsschädlich sein. Sie erkennen sie an Piktogrammen mit rotem Rand auf weißem Grund.",
            pictos: ["ghs-flamme", "ghs-aetzend", "ghs-giftig", "ghs-reizend"],
            scene: "think",
          },
          {
            title: "Die weiteren Zeichen",
            text: "Explosiv, brandfördernd, Gas unter Druck, schwere Gesundheitsgefahr, umweltgefährlich: Jedes Zeichen sagt in einem Bild, wovor Sie sich schützen müssen. Lesen Sie zusätzlich das Etikett: Das Signalwort „Gefahr“ steht für ein höheres Risiko als „Achtung“.",
            pictos: ["ghs-explosiv", "ghs-brandfoerdernd", "ghs-gasflasche", "ghs-gesundheit", "ghs-umwelt"],
            scene: "point",
          },
          {
            title: "Betriebsanweisung und Datenblatt",
            text: "Für jeden Gefahrstoff gibt es eine Betriebsanweisung. Sie sagt, wie Sie ihn benutzen, welche Schutzausrüstung Sie tragen und was im Notfall zu tun ist. Mehr steht im Sicherheitsdatenblatt. Fragen Sie nach, wenn eine Anweisung fehlt.",
            scene: "hello",
          },
          {
            title: "Sicher umgehen",
            text: "Essen, Trinken und Rauchen sind dort tabu, wo mit Gefahrstoffen gearbeitet wird. Füllen Sie nie Chemikalien in Getränkeflaschen um. Behälter immer beschriften und verschlossen lagern. Nach der Arbeit Hände waschen und Haut schützen.",
            pictos: ["haende-waschen", "handschutz", "atemschutz", "augenschutz"],
            scene: "check",
          },
          {
            title: "Wenn etwas passiert",
            text: "Gehen Sie nach der Betriebsanweisung vor. Grundsätzlich gilt: Verunreinigte Kleidung ausziehen und Haut mit viel Wasser spülen. Augen bei geöffnetem Lid lange unter fließendem Wasser spülen. Notruf 112 oder Giftnotruf wählen und das Etikett oder Datenblatt bereithalten.",
            pictos: ["notruftelefon", "erste-hilfe"],
            scene: "alarm",
          },
        ],
        questions: [
          single("aetzend", "Was bedeutet dieses Zeichen?", ["Ätzend: schädigt Haut, Augen und Metalle", "Heiß", "Frisch gestrichen"], 0, "ghs-aetzend"),
          single("flamme", "Dieses Zeichen steht für ...", ["entzündbare Stoffe: Abstand zu Zündquellen halten", "besonders kalte Stoffe", "besonders saubere Stoffe"], 0, "ghs-flamme"),
          single("flasche", "Darf Lösemittel in eine leere Getränkeflasche umgefüllt werden?", ["Ja, wenn ich sie beschrifte", "Nein, nie", "Ja, wenn es niemand sieht"], 1),
          single(
            "anweisung",
            "Wo steht, wie Sie mit einem Gefahrstoff umgehen und was im Notfall zu tun ist?",
            ["In der Betriebsanweisung und im Sicherheitsdatenblatt", "Auf dem Preisschild", "Nirgends, man merkt es schon"],
            0,
          ),
        ],
      },
      {
        id: "ordnung",
        sources: ["BGN „Stolper-, Rutsch- und Sturzunfälle“ und „Heben und Tragen“ (bgn.de)"],
        kind: "basis",
        topics: ["ordnung"],
        title: "Ordnung, Leitern und Heben",
        summary: "Stolpern und Stürzen vermeiden, Leitern sicher benutzen, Lasten richtig heben.",
        noteHint: "Zum Beispiel: Wo stehen Leitern und Tritte? Welche Hilfsmittel gibt es zum Tragen?",
        slides: [
          {
            title: "Stolpern, Rutschen, Stürzen",
            text: "Stolpern, Rutschen und Stürzen sind der Unfallschwerpunkt Nummer eins. Beseitigen Sie Hindernisse sofort oder melden Sie sie, wischen Sie Verschüttetes umgehend auf und benutzen Sie auf Treppen den Handlauf. Tragen Sie festes Schuhwerk mit rutschhemmender Sohle.",
            pictos: ["warn-stolpern", "warn-rutschen"],
            scene: "alarm",
          },
          {
            title: "Leitern",
            text: "Prüfen Sie die Leiter vor jeder Benutzung auf Schäden und sicheren Stand. Stellen Sie sie nicht zu steil und nicht zu flach auf, und steigen Sie nicht auf die obersten Sprossen. Leitern sind für kurze Arbeiten gedacht.",
            scene: "point",
          },
          {
            title: "Richtig heben",
            text: "Heben und tragen Sie rückenschonend und halten Sie die Last nah am Körper. Vermeiden Sie einseitiges Tragen und extreme Bewegungen. Schweres heben Sie möglichst zu zweit oder nutzen Hilfsmittel wie Karre oder Hubwagen. Die Last darf Ihnen nicht die Sicht nehmen.",
            scene: "check",
          },
        ],
        questions: [
          single("ursache", "Was ist die häufigste Ursache für Arbeitsunfälle?", ["Stolpern, Rutschen und Stürzen", "Blitzschlag", "Lärm"], 0),
          single(
            "leiter",
            "Bevor Sie eine Leiter benutzen ...",
            ["prüfen Sie sie auf Schäden und sicheren Stand.", "stellen Sie sie möglichst steil auf.", "stellen Sie sie auf eine Kiste, um höher zu kommen."],
            0,
          ),
          single(
            "heben",
            "Wie heben Sie eine schwere Kiste richtig?",
            ["Aus dem Rücken mit gestreckten Beinen", "Aus den Knien, Rücken gerade, Last nah am Körper", "Mit ausgestreckten Armen weit vor dem Körper"],
            1,
          ),
          single("stolpern", "Was zeigt dieses Zeichen?", ["Warnung vor Hindernissen am Boden, Stolpergefahr", "Baustelle voraus", "Treppe nach oben"], 0, "warn-stolpern"),
        ],
      },
      {
        id: "elektrisch",
        kind: "optional",
        topics: ["maschinen"],
        title: "Elektrische Geräte sicher nutzen",
        summary: "Sichtprüfung vor der Benutzung, defekte Geräte aussortieren, Arbeiten an Anlagen nur durch Fachleute.",
        noteHint: "Zum Beispiel: Wohin kommen defekte Geräte? Wer prüft die Geräte regelmäßig?",
        slides: [
          {
            title: "Erst ansehen, dann einstecken",
            text: "Sehen Sie Kabel, Stecker und Gehäuse vor jeder Benutzung kurz an. Geknickte oder angeschmorte Kabel, lose Stecker oder Risse im Gehäuse sind ein Grund, das Gerät nicht zu benutzen.",
            pictos: ["warn-spannung"],
            scene: "check",
          },
          {
            title: "Defekt heißt: aus dem Verkehr",
            text: "Ein defektes Gerät wird gekennzeichnet, beiseitegelegt und gemeldet. Reparieren Sie es nicht selbst. Arbeiten an elektrischen Anlagen machen nur Elektrofachkräfte oder Personen, die von ihnen angeleitet werden.",
            pictos: ["warn-spannung"],
            scene: "alarm",
          },
          {
            title: "Verlängerungen und Kabeltrommeln",
            text: "Stecken Sie Mehrfachsteckdosen nicht hintereinander. Rollen Sie Kabeltrommeln bei großen Geräten ganz ab, sonst können sie heiß werden. Draußen und bei Nässe nur Geräte und Kabel verwenden, die dafür gemacht sind.",
            scene: "point",
          },
        ],
        questions: [
          single(
            "wer",
            "Wer darf an elektrischen Anlagen arbeiten?",
            ["Jeder, der sich das zutraut", "Elektrofachkräfte oder Personen, die von ihnen angeleitet werden", "Jeder, der Handschuhe trägt"],
            1,
          ),
          multi("sicht", "Was gehört zur Sichtprüfung vor der Benutzung?", ["Kabel und Stecker auf Schäden ansehen", "Das Gehäuse auf Risse prüfen", "Das Gerät kurz in Wasser tauchen"], [0, 1]),
          single("trommel", "Warum rollen Sie eine Kabeltrommel bei großen Geräten ganz ab?", ["Damit sie nicht heiß wird", "Damit sie besser aussieht", "Das ist egal"], 0),
          single("spannung", "Was warnt dieses Zeichen?", ["Vor gefährlicher elektrischer Spannung", "Vor Funkempfang", "Vor Kälte"], 0, "warn-spannung"),
        ],
      },
    ],
  },
];
